/**
 * The bundled charting library (CL v24.004) sizes its canvas bitmaps from
 * ResizeObserver's `device-pixel-content-box` when the browser reports it,
 * but scales its text by `devicePixelRatio`. Where the two disagree (seen at
 * every devicePixelRatio >= 2 viewport in Chromium device emulation), the
 * bitmap stays at CSS size while text is drawn 2-3x, so price-axis labels
 * blow up and get clipped by the fixed-width axis.
 *
 * Making `observe(..., { box: "device-pixel-content-box" })` throw inside the
 * chart iframe sends the library down its other supported path (the one
 * Safari always takes): bitmap = CSS size x devicePixelRatio, watched through
 * a resolution media query. Text and bitmap then agree and stay crisp.
 *
 * Must run synchronously right after `new widget(...)`: the widget inserts its
 * same-origin iframe synchronously, and the library scripts inside it load
 * asynchronously, so the patch is in place before they read it. The iframe
 * keeps its Window object when it navigates from the initial about:blank to
 * the same-origin blob document, so the patch survives that navigation.
 */
export const pinChartCanvasSizing = (container: HTMLElement | null) => {
  const iframe = container?.querySelector("iframe");
  const frameWindow = iframe?.contentWindow as
    | (Window & { ResizeObserver?: typeof ResizeObserver })
    | null
    | undefined;
  const NativeResizeObserver = frameWindow?.ResizeObserver;
  if (!frameWindow || !NativeResizeObserver) return;

  class DevicePixelBoxlessResizeObserver extends NativeResizeObserver {
    observe(target: Element, options?: ResizeObserverOptions) {
      if (options?.box === "device-pixel-content-box") {
        throw new TypeError("device-pixel-content-box is disabled");
      }
      super.observe(target, options);
    }
  }

  try {
    Object.defineProperty(frameWindow, "ResizeObserver", {
      configurable: true,
      writable: true,
      value: DevicePixelBoxlessResizeObserver,
    });
  } catch {
    // Leave the library on its default sizing if the frame refuses the patch.
  }
};
